import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './styles.css';

import { bootstrapSession, onSessionChange } from './app/api.js';
import { AppProviders } from './app/AppProviders.js';
import { clearCacheWhenPersonChanges, createQueryClient } from './app/queryClient.js';
import { buildRouter } from './app/router.js';

/**
 * The shell's entry point.
 *
 * ── THE SESSION IS RESOLVED BEFORE THE FIRST RENDER, AND THAT IS THE WHOLE TRICK ──
 * The access token lives in memory only (see `app/api.ts`), so a page reload always starts
 * with no token and a refresh cookie that may or may not still be good. `bootstrapSession()`
 * exchanges the cookie BEFORE the router mounts.
 *
 * Doing it in an effect instead is the obvious implementation and it is visibly wrong: the
 * router's `beforeLoad` guard runs on the first render, sees no user, and redirects to
 * `/login` — so a signed-in operator gets a flash of the login form on every reload, and any
 * deep link they followed is lost to the redirect before the refresh has even answered.
 *
 * The cost is that `index.html` is on screen with an empty `#root` for one round trip to
 * loopback. That is the right trade, and it is why the loading text is in the markup here
 * rather than being a component.
 *
 * ── AND `StrictMode` IS BACK ON ────────────────────────────────────────────
 * WP4's harness turned it off for a real reason: double-invoked effects mean two REGISTERs
 * and an UNREGISTER against a live switch every time the page mounts, which makes
 * `pjsip show contacts` unreadable exactly when you are trying to read it. That reason is
 * specific to the softphone, and `src/dev/main.tsx` still opts out. Everything in the shell
 * benefits from the double-invoke — it is the check that finds an effect with a missing
 * cleanup, which for this app means a leaked event subscription or a timer that survives a
 * route change.
 */

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root is missing from index.html');

const router = buildRouter();

/**
 * A session that dies in a background query has to reach the router.
 *
 * `createQueryClient` reports a 401 that survived `api.ts`'s refresh-and-retry; the guard on
 * `authenticatedRoute` then does the redirecting, so `invalidate()` is all that is needed —
 * re-running `beforeLoad` is what performs the redirect. Navigating to `/login` from here
 * would duplicate that rule in a second place and lose the `redirect` search param.
 */
const queryClient = createQueryClient(() => {
  void router.invalidate();
});

/**
 * A different person signing in on this tab must not see — or REGISTER WITH — the previous
 * person's cached data (their softphone's SIP credentials above all). Registered before the
 * router's listener below, so the cache is empty by the time the guards re-run. See
 * `clearCacheWhenPersonChanges` for why a token refresh, which fires the same event, clears
 * nothing.
 */
clearCacheWhenPersonChanges(queryClient, onSessionChange);

/**
 * The same for a session that changes for any other reason — a sign-out in this tab, or a
 * refresh that failed. Without this, signing out leaves the last authenticated page on screen
 * until something else happens to trigger a navigation.
 */
onSessionChange(() => {
  void router.invalidate();
});

/**
 * ── AN ASYNC IIFE, NOT A TOP-LEVEL `await` ─────────────────────────────────
 * The `await` version is tidier and esbuild refuses it: "Top-level await is not available in
 * the configured target environment (chrome87, edge88, es2020, firefox78, safari14)". Raising
 * `build.target` would make it compile — and would also drop Safari 14, which is a real
 * consideration for a product whose whole point is a softphone on a phone. The IIFE costs one
 * level of indentation and changes no browser support.
 */
void (async (): Promise<void> => {
  const initialUser = await bootstrapSession();

  createRoot(rootElement).render(
    <StrictMode>
      {/*
        The whole provider stack is `AppProviders`, and this file deliberately assembles
        nothing itself. A JSX tree in an entry point is a tree no test ever renders — which is
        exactly how `ToastProvider` went missing and took both of WP6's pages down.
      */}
      <AppProviders queryClient={queryClient} initialUser={initialUser}>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
})();
